import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfirmationProvider } from './contexts/ConfirmationContext';
import { NotificationProvider } from './components/framework/NotificationProvider';
import { VirtualLabDatasetProvider } from './contexts/VirtualLabDatasetContext';
import GlobalLoader from './components/GlobalLoader';
import ScrollToTop from './components/ScrollToTop';


import './styles/main.css';
import 'bootstrap/dist/css/bootstrap.min.css';

import HomePage from './pages/HomePage';
import DataSubmissionPage from './pages/DataSubmissionPage';
import DatasetPage from "./pages/DatasetPage";
import GettingInvolvedPage from './pages/GettingInvolvedPage';
// import ContributorsPage from './pages/ContributorsPage';
import AboutPage from './pages/AboutPage';
import SupportPage from './pages/SupportPage';
import HowToCitePage from './pages/HowToCitePage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import LicensePage from './pages/LicensePage';
import Layout from './components/Layout';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import EmailCenterPage from './pages/admin/email/EmailCenterPage';
import PeoplePage from './pages/PeoplePage.js';

import DataSubmissionWrapper from "./components/DataSubmission/DataSubmissionWrapper";
import ContactAndFeedbackPage from "./pages/ContactAndFeedbackPage";
import FeedbackPage from "./pages/FeedbackPage";
import PreviewDatasetPage from "./components/preview/DatasetPreviewPage";
import Moderation from "./components/moderation/Moderation";
import FeedbackListPage from './pages/admin/FeedbackListPage'; // adjust path as needed
import MetadataManagement from './pages/admin/MetadataManagement';
import DatasetManagement from './pages/admin/DatasetManagement';


import Login from './pages/admin/Login';
import Register from './pages/Register';
import EmailVerification from './pages/EmailVerification';
import VerificationPending from './pages/VerificationPending';
import ProfileSettings from './pages/ProfileSettings';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

import ManageEntries from './components/admin/ManageEntries';
import UserManagement from './pages/admin/UserManagement';
import CasSearchPage from './pages/admin/CasSearchPage';


import AdminDashboard from './pages/admin/AdminDashboard';

import ProtectedRoute from './components/ProtectedRoute';
import MySubmissionsPage from './pages/MySubmissionsPage';
import BulkImportPage from './pages/BulkImportPage';
import PublicationsPage from './pages/PublicationsPage';
import PartnersPage from './pages/PartnersPage';

// Virtual Lab Imports - Lazy Loaded
const VirtualLabLayout = React.lazy(() => import('./components/virtual-lab/VirtualLabLayout'));
const LabDatasets = React.lazy(() => import('./pages/virtual-lab/LabDatasets'));
const LabEDA = React.lazy(() => import('./pages/virtual-lab/LabEDA'));
const LabTraining = React.lazy(() => import('./pages/virtual-lab/LabTraining'));
const LabExplainability = React.lazy(() => import('./pages/virtual-lab/LabExplainability'));
const LabModels = React.lazy(() => import('./pages/virtual-lab/LabModels'));
const RecipeBuilderPage = React.lazy(() => import('./pages/virtual-lab/RecipeBuilderPage'));
const VirtualLabRouteWrapper = React.lazy(() => import('./components/virtual-lab/VirtualLabRouteWrapper'));


const AppRoutes = () => {
  const { loading } = useAuth();

  if (loading) {
    return <GlobalLoader />;
  }

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        <Route path="/home" element={<Layout><HomePage /></Layout>} />
        <Route path="/dataSubmission" element={<Layout><DataSubmissionPage /></Layout>} />
        <Route path="/dataset" element={<Layout><DatasetPage /></Layout>} />
        <Route path="/about" element={<Layout><AboutPage /></Layout>} />
        <Route path="/support" element={<Layout><SupportPage /></Layout>} />
        <Route path="/how-to-cite" element={<Layout><HowToCitePage /></Layout>} />
        <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
        <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
        <Route path="/license" element={<Layout><LicensePage /></Layout>} />
        <Route path="/partners" element={<Layout><PartnersPage /></Layout>} />
        {/* <Route path="/people" element={<Layout><PeoplePage /></Layout>} /> */}
        {/* <Route path="/contributors" element={<Layout><ContributorsPage /></Layout>} /> */}
        <Route path="/community" element={<Layout><GettingInvolvedPage /></Layout>} />
        <Route path="/login" element={<Layout><Login /></Layout>} />
        <Route path="/register" element={<Layout><Register /></Layout>} />
        <Route path="/verify-email" element={<Layout><EmailVerification /></Layout>} />
        <Route path="/verification-pending" element={<Layout><VerificationPending /></Layout>} />
        <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
        <Route path="/reset-password" element={<Layout><ResetPassword /></Layout>} />
        <Route path="/profile" element={<Layout><ProfileSettings /></Layout>} />
        <Route path="/admin" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
        <Route path="/electrospinningTable" element={<Layout><DataSubmissionWrapper /></Layout>} />
        <Route path="/feedbackPage" element={<Layout><FeedbackPage /></Layout>} />
        <Route path="/previewDatasetPage" element={<Layout><PreviewDatasetPage /></Layout>} />
        <Route path="/moderationPage" element={<ProtectedAdminRoute allowedRoles={['ADMIN', 'MODERATOR']}><Moderation /></ProtectedAdminRoute>} />
        <Route path="/contact" element={<Layout><ContactAndFeedbackPage /></Layout>} />
        <Route path="/admin/feedback" element={<ProtectedAdminRoute><FeedbackListPage /></ProtectedAdminRoute>} />
        <Route path="/admin/manage-entries" element={<ProtectedAdminRoute><ManageEntries /></ProtectedAdminRoute>} />
        <Route path="/admin/users" element={<ProtectedAdminRoute><UserManagement /></ProtectedAdminRoute>} />
        <Route path="/admin/metadata" element={<ProtectedAdminRoute><MetadataManagement /></ProtectedAdminRoute>} />
        <Route path="/admin/datasets" element={<ProtectedAdminRoute><DatasetManagement /></ProtectedAdminRoute>} />
        <Route path="/admin/cas-search" element={<ProtectedAdminRoute><CasSearchPage /></ProtectedAdminRoute>} />
        <Route path="/admin/email" element={<ProtectedAdminRoute><EmailCenterPage /></ProtectedAdminRoute>} />
        <Route path="/admin/email/:section" element={<ProtectedAdminRoute><EmailCenterPage /></ProtectedAdminRoute>} />
        <Route path="/admin/email/send" element={<Navigate to="/admin/email" replace />} />


        <Route path="/publications" element={<Layout><PublicationsPage /></Layout>} />


        {/* User Submission Management */}
        <Route path="/my-submissions" element={<ProtectedRoute><Layout><MySubmissionsPage /></Layout></ProtectedRoute>} />
        <Route path="/bulk-upload" element={<ProtectedRoute><Layout><BulkImportPage /></Layout></ProtectedRoute>} />
        <Route path="/edit-submission/:id" element={<ProtectedRoute><Layout><DataSubmissionWrapper /></Layout></ProtectedRoute>} />


        {/* Virtual Lab Routes */}
        <Route path="/virtual-lab" element={<React.Suspense fallback={<GlobalLoader />}><VirtualLabRouteWrapper /></React.Suspense>} />
        <Route path="/virtual-lab/datasets" element={<ProtectedRoute><React.Suspense fallback={<GlobalLoader />}><VirtualLabLayout><LabDatasets /></VirtualLabLayout></React.Suspense></ProtectedRoute>} />


        <Route path="/virtual-lab/eda" element={<ProtectedRoute><React.Suspense fallback={<GlobalLoader />}><VirtualLabLayout><LabEDA /></VirtualLabLayout></React.Suspense></ProtectedRoute>} />
        <Route path="/virtual-lab/training" element={<ProtectedRoute><React.Suspense fallback={<GlobalLoader />}><VirtualLabLayout><LabTraining /></VirtualLabLayout></React.Suspense></ProtectedRoute>} />
        <Route path="/virtual-lab/recipe-builder" element={<ProtectedRoute><React.Suspense fallback={<GlobalLoader />}><VirtualLabLayout><RecipeBuilderPage /></VirtualLabLayout></React.Suspense></ProtectedRoute>} />
        <Route path="/virtual-lab/models" element={<ProtectedRoute><React.Suspense fallback={<GlobalLoader />}><VirtualLabLayout><LabModels /></VirtualLabLayout></React.Suspense></ProtectedRoute>} />
        <Route path="/virtual-lab/explainability" element={<ProtectedRoute><React.Suspense fallback={<GlobalLoader />}><VirtualLabLayout><LabExplainability /></VirtualLabLayout></React.Suspense></ProtectedRoute>} />
      </Routes>
    </Router>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <ConfirmationProvider>
        <NotificationProvider>
          <VirtualLabDatasetProvider>
            <AppRoutes />
          </VirtualLabDatasetProvider>
        </NotificationProvider>
      </ConfirmationProvider>
    </AuthProvider>
  );
};

export default App;
